import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "travel-details" }
const _hoisted_2 = {
  key: 0,
  class: "travel-details__departure"
}
const _hoisted_3 = { class: "travel-details__airport-info" }
const _hoisted_4 = { class: "travel-details__airport-name" }
const _hoisted_5 = { class: "travel-details__departure-time" }
const _hoisted_6 = ["datetime", "textContent"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = ["datetime"]
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { class: "travel-details__update-date" }
const _hoisted_11 = { class: "travel-details__airline" }

import { formatDateWeekdayPoint } from '@utils/dateTimeUtils';
import type { VerifiedFlightSegment, UnverifiedFlight } from '@interfaces/offer';
import { increaseDate } from '@utils/dateTimeUtils';
import { computed } from 'vue';
import { NBSP } from '@global-js/constants';
import TravelDateAttribute from '@lmt-rpb/Atoms/TravelDateAttribute/TravelDateAttribute.vue';
import { useTranslations } from '../../composables/translations';

interface Props {
	flightSegments?: VerifiedFlightSegment[];
	flight?: UnverifiedFlight;
	inbound?: boolean;
	unverifiedDate?: string | Date;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TravelDetails',
  props: {
    flightSegments: { default: () => [] },
    flight: { default: undefined },
    inbound: { type: Boolean, default: false },
    unverifiedDate: { default: '' }
  },
  setup(__props: any) {

const props = __props;

// Handle verified flight segments
const firstFlightSegment = computed(() => props.flightSegments?.[0]);
const { translate } = useTranslations();
const formattedDate = computed((): string => {
	return formatDateWeekdayPoint(departureDate.value);
});

const lastFlightSegment = computed(() => props.flightSegments?.[props.flightSegments.length - 1]);

const stops = computed((): number => {
	if (props.flightSegments && props.flightSegments.length > 0) {
		return props.flightSegments.length - 1;
	}
	if (props.flight?.NumberOfStops !== undefined) {
		return props.flight.NumberOfStops;
	}
	return 0;
});

const directness = computed((): string => {
	if (props.flight?.DirectFlightAssumed) {
		return translate('offer.flight.stops', 0);
	}
	return translate('offer.flight.stops', { stop: stops.value }, stops.value);
});

const unverifiedDirectness = computed(() => {
	if (props.flight?.DirectFlightAssumed && props.flight.NumberOfStops === 0) {
		return translate('offer.flight.stops', 0);
	}
	if (props.flight?.NumberOfStops && props.flight?.NumberOfStops > 0 && !props.flight?.DirectFlightAssumed) {
		return translate('offer.flight.stops', { stop: props.flight?.NumberOfStops }, props.flight?.NumberOfStops);
	}
	return '';
});

const departureDate = computed((): string | Date => {
	if (firstFlightSegment.value?.Departure?.Date) {
		return firstFlightSegment.value.Departure.Date;
	}
	if (props.flight?.Departure?.Date) {
		return props.flight.Departure.Date;
	}
	return props.unverifiedDate;
});

// Unified flight data for template
const flightData = computed(() => {
	if (!firstFlightSegment.value && !props.flight) {
		return null;
	}

	// For verified flight segments
	if (firstFlightSegment.value && lastFlightSegment.value) {
		return {
			departureAirport: firstFlightSegment.value.Departure.Airport,
			departureTime: firstFlightSegment.value.Departure.time,
			arrivalTime: lastFlightSegment.value.Arrival.time,
			arrivalDate: lastFlightSegment.value.Arrival.Date,
			directness: directness.value,
			airline: firstFlightSegment.value.Airline,
		};
	}

	// For unverified flight
	if (props.flight) {
		return {
			departureAirport: props.flight.Departure.Airport,
			departureTime: props.flight.Departure.time,
			arrivalTime: props.flight.Arrival.time,
			arrivalDate: props.flight.Arrival.Date,
			directness: unverifiedDirectness.value,
			airline: props.flight.Airline?.Name,
		};
	}

	return null;
});

const arrivalDate = computed((): string | Date => {
	return lastFlightSegment?.value?.Arrival?.Date || props.flight?.Arrival?.Date || props.unverifiedDate;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TravelDateAttribute, {
      class: "travel-details__date",
      icon: ['fa-solid', 'fa-plane'],
      text: formattedDate.value,
      "icon-class": { 'is-inbound': _ctx.inbound }
    }, null, 8 /* PROPS */, ["text", "icon-class"]),
    (flightData.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", _hoisted_4, _toDisplayString(flightData.value.departureAirport) + _toDisplayString(_unref(NBSP)), 1 /* TEXT */)
          ]),
          _createElementVNode("div", _hoisted_5, [
            (flightData.value.departureTime)
              ? (_openBlock(), _createElementBlock("time", {
                  key: 0,
                  class: "travel-details__flight-departure-time",
                  datetime: flightData.value.departureTime?.toString(),
                  textContent: _toDisplayString(flightData.value.departureTime)
                }, null, 8 /* PROPS */, _hoisted_6))
              : (_openBlock(), _createElementBlock("span", _hoisted_7, "--")),
            _cache[0] || (_cache[0] = _createTextVNode(" – ")),
            (flightData.value.arrivalTime)
              ? (_openBlock(), _createElementBlock("time", {
                  key: 2,
                  class: "travel-details__flight-arrival-time",
                  datetime: flightData.value.arrivalDate?.toString()
                }, _toDisplayString(flightData.value.arrivalTime), 9 /* TEXT, PROPS */, _hoisted_8))
              : (_openBlock(), _createElementBlock("span", _hoisted_9, " -- ")),
            _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(increaseDate)(departureDate.value, arrivalDate.value)), 1 /* TEXT */)
          ]),
          _withDirectives(_createElementVNode("div", { class: "travel-details__directness" }, _toDisplayString(flightData.value.directness), 513 /* TEXT, NEED_PATCH */), [
            [_vShow, flightData.value.directness]
          ]),
          _createElementVNode("div", _hoisted_11, _toDisplayString(flightData.value.airline), 1 /* TEXT */)
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})